/**
 * CenikImportReview — review/diff step of the Nissan XLSX ceník import.
 *
 * Renders the AI-extracted draft grouped by resolved vozidlo_model (several
 * sheets — MY24 + MY26, VAN + PC — can merge into one model section). Per
 * section the draft is diffed against the model's active entities
 * (cenik-import-helpers.ts): rows are Nová / Nahrazuje #id (with price
 * deltas), and unclaimed active entities list as "Chybí v souboru" with a
 * Ponechat (default) / Deaktivovat choice. Sheets carrying no upholstery
 * block (Townstar, Interstar) get a "doplnit interiéry" panel — one
 * hand-typed interior applies to any selection of the affected entities, and
 * the commit is blocked while an included entity has none, since a vehicle or
 * offer can't be created without picking an interior. Nothing is written
 * until "Zapsat do ceníku" posts the whole reviewed body to the transactional
 * commit endpoint.
 */

import { Fragment, useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { useConfirm } from '@/components/ConfirmDialog';
import { Field } from '@/components/Field';
import { Icon } from '@/components/Icon';
import { SegmentedControl } from '@/components/SegmentedControl';
import { Skeleton } from '@/components/Skeleton';
import { TextInput } from '@/components/TextInput';
import { toast } from '@/components/Toaster';
import { api } from '@/lib/api';
import { formatInt, pluralCs } from '@/lib/format';
import {
  useCenikImportCommitMutation,
  useMgmtModelsQuery,
  type CenikImportCommitBody,
  type CenikImportDraft,
  type CenikImportItemDraft,
  type MgmtEntity,
} from '@/modules/management/api';
import { diffEntities, type CenikImportDiffRow } from './cenik-import-helpers';

interface CenikImportReviewProps {
  draft: CenikImportDraft;
  brandId: number;
  onClose: () => void;
}

/**
 * Stable key of one imported row within the whole draft — sheet name plus the
 * row's position in that sheet, so review state survives a model remapping
 * (which reshuffles the merged diff rows).
 */
const rowKey = (list: string, poradi: number) => `${list}#${poradi}`;

/** Interiors of a row after the manual fill-in (file first, hand-typed after). */
function interieryRadku(
  row: CenikImportDiffRow,
  doplnene: Record<string, CenikImportItemDraft[]>,
): CenikImportItemDraft[] {
  return [...row.draft.interiery, ...(doplnene[rowKey(row.list, row.poradi)] ?? [])];
}

/** Rows the admin left checked — exactly what the commit would write. */
const zapisovaneRadky = (rows: CenikImportDiffRow[], vyrazene: Set<string>) =>
  rows.filter((r) => !vyrazene.has(rowKey(r.list, r.poradi)));

/** `'12 500,5'` → 12500.5 — same free-form entry as the ceník item form. */
function parseCena(input: string): number {
  return Number.parseFloat(input.replace(/\s/g, '').replace(',', '.'));
}

const cellBadge = 'inline-block px-1.5 py-0.5 font-mono text-[9.5px] uppercase tracking-[0.08em]';

function formatDelta(nova: number, stara: number): string {
  const d = nova - stara;
  const sign = d > 0 ? '+' : '−';
  return `${formatInt(stara)} → ${formatInt(nova)} (${sign}${formatInt(Math.abs(d))})`;
}

export function CenikImportReview({ draft, brandId, onClose }: CenikImportReviewProps) {
  const { confirm, dialog: confirmDialog } = useConfirm();
  const commit = useCenikImportCommitMutation();
  const models = useMgmtModelsQuery(brandId);

  // Per-sheet model mapping (server auto-match as the default), per-row
  // exclusion, and the deactivation picks for missing-from-file entities.
  const [mapovani, setMapovani] = useState<Record<string, number | null>>(() =>
    Object.fromEntries(draft.modely.map((m) => [m.list, m.vozidlo_model])),
  );
  const [vyrazene, setVyrazene] = useState<Set<string>>(new Set());
  const [deaktivace, setDeaktivace] = useState<Set<number>>(new Set());
  const [rozbalene, setRozbalene] = useState<Set<string>>(new Set());
  // Hand-typed interiors per row key, for sheets the pricelist ships without.
  const [doplnene, setDoplnene] = useState<Record<string, CenikImportItemDraft[]>>({});

  // Sheets grouped by their (re)mapped model — the merge step.
  const sekce = useMemo(() => {
    const byModel = new Map<number, typeof draft.modely>();
    const nemapovane: typeof draft.modely = [];
    for (const m of draft.modely) {
      const id = mapovani[m.list] ?? null;
      if (id == null) {
        nemapovane.push(m);
      } else {
        const bucket = byModel.get(id);
        if (bucket) bucket.push(m);
        else byModel.set(id, [m]);
      }
    }
    return { byModel: [...byModel.entries()], nemapovane };
  }, [draft.modely, mapovani]);

  // Active entities of every mapped model — same cache keys as the editor.
  const entityQueries = useQueries({
    queries: sekce.byModel.map(([modelId]) => ({
      queryKey: ['management', 'entities', modelId],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        api.get<{ entities: MgmtEntity[] }>(`/v1/management/entities?vozidlo_model=${modelId}`, {
          signal,
        }),
    })),
  });
  const entitiesLoading = entityQueries.some((q) => q.isLoading);

  const diffy = useMemo(
    () =>
      sekce.byModel.map(([modelId, sheets], i) => ({
        modelId,
        sheets,
        diff: diffEntities(
          sheets.map((s) => ({ list: s.list, entity: s.entity })),
          entityQueries[i]?.data?.entities ?? [],
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- data snapshots drive this
    [sekce.byModel, entityQueries.map((q) => q.data).join('')],
  );

  const toggleVyrazeni = (key: string) =>
    setVyrazene((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggleRozbaleni = (key: string) =>
    setRozbalene((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const pridatInterier = (klice: string[], item: CenikImportItemDraft) =>
    setDoplnene((prev) => {
      const next = { ...prev };
      for (const k of klice) next[k] = [...(next[k] ?? []), item];
      return next;
    });

  const odebratInterier = (klic: string, index: number) =>
    setDoplnene((prev) => {
      const zbytek = (prev[klic] ?? []).filter((_, i) => i !== index);
      const next = { ...prev };
      if (zbytek.length > 0) next[klic] = zbytek;
      else delete next[klic];
      return next;
    });

  const zapsat = () => {
    const body: CenikImportCommitBody = {
      zdroj_klic: draft.zdrojKlic,
      zdroj_nazev: draft.zdrojNazev,
      modely: diffy
        .map(({ modelId, diff }) => ({
          vozidlo_model: modelId,
          entity: zapisovaneRadky(diff.rows, vyrazene).map((r) => ({
            provedeni: r.draft.provedeni,
            verze: r.draft.verze,
            motor: r.draft.motor,
            prevodovka: r.draft.prevodovka,
            cenikova_cena: r.draft.cenikova_cena,
            nakupni_cena: r.draft.nakupni_cena,
            nahrazuje_id: r.nahrazuje?.id ?? null,
            barvy: r.draft.barvy,
            interiery: interieryRadku(r, doplnene),
            vybavy: r.draft.vybavy,
          })),
          deaktivovat: diff.chybejici.filter((e) => deaktivace.has(e.id)).map((e) => e.id),
        }))
        .filter((m) => m.entity.length > 0 || m.deaktivovat.length > 0),
    };
    if (body.modely.length === 0) {
      toast.warn('Není co zapsat — všechny řádky jsou vyřazené.');
      return;
    }
    // An entity with no interior can't be picked in the vehicle/offer wizard,
    // so it must not reach the catalog — doplnit, or vyřadit the row.
    const bezInterieru = body.modely.reduce(
      (n, m) => n + m.entity.filter((e) => e.interiery.length === 0).length,
      0,
    );
    if (bezInterieru > 0) {
      toast.warn(
        `${formatInt(bezInterieru)} ${pluralCs(bezInterieru, ['entita nemá', 'entity nemají', 'entit nemá'])} interiér — ` +
          'doplňte jej v sekci „Ceník neuvádí interiéry", nebo řádky vyřaďte.',
      );
      return;
    }
    commit.mutate(body, {
      onSuccess: (r) => {
        toast.ok(
          `Ceník zapsán — ${formatInt(r.entityVlozeno)} nových, ${formatInt(r.entityNahrazeno)} nahrazených, ` +
            `${formatInt(r.entityDeaktivovano)} deaktivovaných entit (${formatInt(r.vybavyPreneseno)} výbav přeneseno).`,
        );
        onClose();
      },
      onError: (err) => toast.error(err instanceof Error ? err.message : 'Zápis ceníku selhal'),
    });
  };

  const zahodit = async () => {
    const ok = await confirm({
      title: 'Zahodit vytěžený ceník?',
      description: 'Zkontrolované řádky se neuloží. Zdrojový sešit zůstane uložen.',
      confirmLabel: 'Zahodit',
    });
    if (ok) onClose();
  };

  const celkemEntit = draft.modely.reduce((n, m) => n + m.entity.length, 0);

  return (
    <section data-testid="cenik-import-review" className="flex flex-col gap-5">
      {confirmDialog}

      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink-2">
            Kontrola importu — {draft.zdrojNazev}
          </h3>
          <p className="mt-0.5 text-[12px] text-ink-3">
            {formatInt(draft.modely.length)} listů · {formatInt(celkemEntit)} entit ke kontrole.
            Zápisem vzniknou nové aktivní řádky; nahrazené zůstávají v historii. Ruční výbavy
            nahrazovaných entit budou přeneseny automaticky.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button variant="secondary" size="sm" onClick={zahodit} disabled={commit.isPending}>
            Zahodit
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={<Icon name="check" size={12} />}
            onClick={zapsat}
            disabled={commit.isPending || entitiesLoading}
          >
            {commit.isPending ? 'Zapisuji…' : 'Zapsat do ceníku'}
          </Button>
        </div>
      </div>

      {draft.chyby.map((ch) => (
        <p key={ch.list} className="border border-line bg-paper px-4 py-3 text-[12px] text-warn">
          List „{ch.list}" se nepodařilo vytěžit: {ch.chyba}
        </p>
      ))}

      {sekce.nemapovane.map((m) => (
        <ModelHeader
          key={m.list}
          sheets={[m]}
          models={models.data?.models ?? []}
          value={null}
          onChange={(id) => setMapovani((prev) => ({ ...prev, [m.list]: id }))}
          note="Model nepřiřazen — list bude přeskočen."
        />
      ))}

      {entitiesLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : (
        diffy.map(({ modelId, sheets, diff }) => (
          <div key={modelId} className="flex flex-col gap-2">
            <ModelHeader
              sheets={sheets}
              models={models.data?.models ?? []}
              value={modelId}
              onChange={(id) => {
                setMapovani((prev) => {
                  const next = { ...prev };
                  for (const s of sheets) next[s.list] = id;
                  return next;
                });
              }}
            />

            <div className="overflow-x-auto border border-line bg-paper">
              <table className="w-full border-collapse text-[11.5px]">
                <thead className="bg-bg">
                  <tr className="text-left font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
                    <th className="w-[36px] px-2 py-2" />
                    <th className="min-w-[150px] px-2 py-2">Provedení</th>
                    <th className="min-w-[70px] px-2 py-2">Verze</th>
                    <th className="min-w-[150px] px-2 py-2">Motor</th>
                    <th className="min-w-[120px] px-2 py-2">Převodovka</th>
                    <th className="min-w-[170px] px-2 py-2 text-right">Ceník bez DPH</th>
                    <th className="min-w-[170px] px-2 py-2 text-right">Nákup bez DPH</th>
                    <th className="min-w-[120px] px-2 py-2">Stav</th>
                    <th className="w-[36px] px-2 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {diff.rows.map((r) => (
                    <ImportRow
                      key={rowKey(r.list, r.poradi)}
                      row={r}
                      interiery={interieryRadku(r, doplnene)}
                      vyrazen={vyrazene.has(rowKey(r.list, r.poradi))}
                      rozbalen={rozbalene.has(rowKey(r.list, r.poradi))}
                      onToggleVyrazeni={() => toggleVyrazeni(rowKey(r.list, r.poradi))}
                      onToggleRozbaleni={() => toggleRozbaleni(rowKey(r.list, r.poradi))}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            <InterieryDoplneni
              rows={zapisovaneRadky(diff.rows, vyrazene).filter(
                (r) => r.draft.interiery.length === 0,
              )}
              doplnene={doplnene}
              onPridat={pridatInterier}
              onOdebrat={odebratInterier}
            />

            {diff.chybejici.length > 0 && (
              <div className="border border-line bg-paper px-4 py-3">
                <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
                  Chybí v souboru — aktivní entity bez protějšku v ceníku
                </p>
                <div className="mt-2 flex flex-col gap-1.5">
                  {diff.chybejici.map((e) => (
                    <div key={e.id} className="flex flex-wrap items-center gap-3 text-[12px]">
                      <span className="text-ink">
                        {e.provedeni || '—'}
                        <span className="ml-1.5 text-ink-3">
                          {e.motor} · {e.prevodovka}
                        </span>
                      </span>
                      <span className="ml-auto">
                        <SegmentedControl<'ponechat' | 'deaktivovat'>
                          aria-label={`Osud entity ${e.provedeni}`}
                          options={[
                            { value: 'ponechat', label: 'Ponechat' },
                            { value: 'deaktivovat', label: 'Deaktivovat' },
                          ]}
                          value={deaktivace.has(e.id) ? 'deaktivovat' : 'ponechat'}
                          onChange={(v) =>
                            setDeaktivace((prev) => {
                              const next = new Set(prev);
                              if (v === 'deaktivovat') next.add(e.id);
                              else next.delete(e.id);
                              return next;
                            })
                          }
                        />
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Section header — sheet names + model mapping select
   ───────────────────────────────────────────────────────────────────────── */

interface ModelHeaderProps {
  sheets: { list: string; model_nazev: string; modelovy_rok: string | null }[];
  models: { id: number; nazev_modelu: string }[];
  value: number | null;
  onChange: (id: number | null) => void;
  note?: string;
}

function ModelHeader({ sheets, models, value, onChange, note }: ModelHeaderProps) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink-2">
        {sheets.map((s) => s.list).join(' + ')}
      </span>
      <label className="flex items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
        Model
        <select
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          className="border border-line bg-paper px-1.5 py-1 font-mono text-[11px] text-ink focus:border-accent focus:outline-none"
        >
          <option value="">— přeskočit —</option>
          {models.map((m) => (
            <option key={m.id} value={m.id}>
              {m.nazev_modelu}
            </option>
          ))}
        </select>
      </label>
      {note && <span className="text-[11.5px] text-warn">{note}</span>}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Manual interior fill-in — sheets the pricelist ships without upholstery
   ───────────────────────────────────────────────────────────────────────── */

interface InterieryDoplneniProps {
  /** Rows headed for the catalog whose sheet carried no interiors. */
  rows: CenikImportDiffRow[];
  doplnene: Record<string, CenikImportItemDraft[]>;
  onPridat: (klice: string[], item: CenikImportItemDraft) => void;
  onOdebrat: (klic: string, index: number) => void;
}

function InterieryDoplneni({ rows, doplnene, onPridat, onOdebrat }: InterieryDoplneniProps) {
  // Deselection is the state, not selection — a row that shows up later (an
  // exclusion taken back) then starts selected instead of silently skipped.
  const [odznacene, setOdznacene] = useState<Set<string>>(new Set());
  const [nazev, setNazev] = useState('');
  const [cenikovaCena, setCenikovaCena] = useState('0');
  const [nakupniCena, setNakupniCena] = useState('0');

  const klice = rows.map((r) => rowKey(r.list, r.poradi));
  const vybrane = klice.filter((k) => !odznacene.has(k));
  const nedoplnene = klice.filter((k) => (doplnene[k] ?? []).length === 0);

  if (rows.length === 0) return null;

  const prepnout = (klic: string) =>
    setOdznacene((prev) => {
      const next = new Set(prev);
      if (next.has(klic)) next.delete(klic);
      else next.add(klic);
      return next;
    });

  const prepnoutVse = () =>
    setOdznacene(vybrane.length === klice.length ? new Set(klice) : new Set());

  const pridat = () => {
    const jmeno = nazev.replace(/\s+/g, ' ').trim();
    if (!jmeno) {
      toast.error('Zadejte název interiéru.');
      return;
    }
    const cenikova = parseCena(cenikovaCena);
    const nakupni = parseCena(nakupniCena);
    if (!Number.isFinite(cenikova) || !Number.isFinite(nakupni)) {
      toast.error('Zadejte platnou číselnou hodnotu cen.');
      return;
    }
    if (vybrane.length === 0) {
      toast.warn('Vyberte alespoň jednu entitu.');
      return;
    }
    onPridat(vybrane, { nazev: jmeno, cenikova_cena: cenikova, nakupni_cena: nakupni });
    toast.ok(
      `Interiér „${jmeno}" přidán ${formatInt(vybrane.length)} ${pluralCs(vybrane.length, ['entitě', 'entitám', 'entitám'])}.`,
    );
    setNazev('');
  };

  return (
    <div className="border border-warn/50 bg-paper px-4 py-3">
      <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-warn">
        Ceník neuvádí interiéry — doplňte ručně
      </p>
      <p className="mt-1 max-w-[80ch] text-[11.5px] text-ink-3">
        {formatInt(rows.length)}{' '}
        {pluralCs(rows.length, ['entita nemá', 'entity nemají', 'entit nemá'])} v souboru žádné
        čalounění — list ho vůbec neobsahuje. Bez interiéru nejde vozidlo ani nabídku založit, proto
        se takové entity nezapíšou. Zadejte čalounění a přiřaďte ho vybraným entitám.
      </p>

      <div className="mt-3 flex flex-wrap items-end gap-3">
        <div className="min-w-[240px] flex-1">
          <Field label="Název interiéru">
            <TextInput
              value={nazev}
              placeholder="např. černé látkové čalounění"
              onChange={(e) => setNazev(e.target.value)}
            />
          </Field>
        </div>
        <div className="w-[150px]">
          <Field label="Ceníková bez DPH">
            <TextInput
              type="number"
              value={cenikovaCena}
              suffix="Kč"
              onChange={(e) => setCenikovaCena(e.target.value)}
            />
          </Field>
        </div>
        <div className="w-[150px]">
          <Field label="Nákupní bez DPH">
            <TextInput
              type="number"
              value={nakupniCena}
              suffix="Kč"
              onChange={(e) => setNakupniCena(e.target.value)}
            />
          </Field>
        </div>
        <Button
          variant="secondary"
          size="sm"
          icon={<Icon name="plus" size={12} />}
          onClick={pridat}
          disabled={vybrane.length === 0}
        >
          Přidat vybraným ({formatInt(vybrane.length)})
        </Button>
      </div>

      <div className="mt-3 flex items-center justify-between gap-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
          Entity bez interiéru
        </span>
        <button
          type="button"
          onClick={prepnoutVse}
          className="font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3 hover:text-ink"
        >
          {vybrane.length === klice.length ? 'Zrušit výběr' : 'Vybrat vše'}
        </button>
      </div>

      <div className="mt-2 flex flex-col gap-1.5">
        {rows.map((r) => {
          const klic = rowKey(r.list, r.poradi);
          const pridane = doplnene[klic] ?? [];
          return (
            <div key={klic} className="flex flex-wrap items-center gap-2 text-[12px]">
              <Checkbox
                checked={!odznacene.has(klic)}
                onChange={() => prepnout(klic)}
                aria-label={`Vybrat ${r.draft.provedeni}`}
              />
              <span className="text-ink">
                {r.draft.provedeni || '—'}
                <span className="ml-1.5 text-ink-3">
                  {[r.draft.verze, r.draft.motor, r.draft.prevodovka].filter(Boolean).join(' · ')}
                </span>
              </span>
              <span className="ml-auto flex flex-wrap items-center gap-1.5">
                {pridane.length === 0 ? (
                  <span className={`${cellBadge} bg-warn/10 text-warn`}>Chybí</span>
                ) : (
                  pridane.map((it, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 border border-line px-1.5 py-0.5 text-[11px] text-ink-2"
                    >
                      {it.nazev}
                      <span className="font-mono tabular-nums text-ink-3">
                        {formatInt(it.cenikova_cena)} Kč
                      </span>
                      <button
                        type="button"
                        onClick={() => onOdebrat(klic, i)}
                        aria-label={`Odebrat interiér ${it.nazev} u ${r.draft.provedeni}`}
                        className="text-ink-3 hover:text-accent-text"
                      >
                        <Icon name="close" size={11} />
                      </button>
                    </span>
                  ))
                )}
              </span>
            </div>
          );
        })}
      </div>

      {nedoplnene.length > 0 && (
        <p className="mt-2 text-[11px] text-warn">
          Zbývá doplnit {formatInt(nedoplnene.length)}{' '}
          {pluralCs(nedoplnene.length, ['entitu', 'entity', 'entit'])} — jinak zápis neproběhne.
        </p>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   One imported entity row + expandable child inspection
   ───────────────────────────────────────────────────────────────────────── */

interface ImportRowProps {
  row: CenikImportDiffRow;
  /** Row interiors incl. the hand-typed ones — what the commit would write. */
  interiery: CenikImportItemDraft[];
  vyrazen: boolean;
  rozbalen: boolean;
  onToggleVyrazeni: () => void;
  onToggleRozbaleni: () => void;
}

function ImportRow({
  row,
  interiery,
  vyrazen,
  rozbalen,
  onToggleVyrazeni,
  onToggleRozbaleni,
}: ImportRowProps) {
  const { draft, nahrazuje } = row;
  // The extractor's missing-interiors warning is stale once one was typed in.
  const varovani =
    interiery.length > 0
      ? draft.varovani.filter((v) => !v.startsWith('Ceník neuvádí interiéry'))
      : draft.varovani;
  return (
    <Fragment>
      <tr className={`border-t border-line align-top ${vyrazen ? 'opacity-40' : ''}`}>
        <td className="px-2 py-1.5">
          <input
            type="checkbox"
            checked={!vyrazen}
            onChange={onToggleVyrazeni}
            aria-label={`Zahrnout ${draft.provedeni}`}
          />
        </td>
        <td className="px-2 py-1.5 text-ink">
          {draft.provedeni}
          {varovani.length > 0 && (
            <div className="mt-0.5 flex flex-col gap-0.5">
              {varovani.map((v, j) => (
                <span key={j} className="text-[10.5px] text-warn">
                  ⚠ {v}
                </span>
              ))}
            </div>
          )}
        </td>
        <td className="px-2 py-1.5 text-ink-2">{draft.verze || '—'}</td>
        <td className="px-2 py-1.5 text-ink-2">{draft.motor}</td>
        <td className="px-2 py-1.5 text-ink-2">{draft.prevodovka}</td>
        <td className="px-2 py-1.5 text-right font-mono tabular-nums text-ink">
          {nahrazuje && nahrazuje.cenikova_cena !== draft.cenikova_cena
            ? formatDelta(draft.cenikova_cena, nahrazuje.cenikova_cena)
            : `${formatInt(draft.cenikova_cena)} Kč`}
        </td>
        <td className="px-2 py-1.5 text-right font-mono tabular-nums text-ink-2">
          {nahrazuje && nahrazuje.nakupni_cena !== draft.nakupni_cena
            ? formatDelta(draft.nakupni_cena, nahrazuje.nakupni_cena)
            : `${formatInt(draft.nakupni_cena)} Kč`}
        </td>
        <td className="px-2 py-1.5">
          {nahrazuje ? (
            <span className={`${cellBadge} bg-bg text-ink-2`}>Nahrazuje #{nahrazuje.id}</span>
          ) : (
            <span className={`${cellBadge} bg-accent/10 text-accent`}>Nová</span>
          )}
        </td>
        <td className="px-2 py-1.5 text-center">
          <button
            type="button"
            onClick={onToggleRozbaleni}
            aria-label={`Detail ${draft.provedeni}`}
            aria-expanded={rozbalen}
            className="text-ink-3 hover:text-ink"
          >
            <Icon name={rozbalen ? 'chevronD' : 'chevronR'} size={13} />
          </button>
        </td>
      </tr>
      {rozbalen && (
        <tr className="border-t border-line bg-bg/40">
          <td />
          <td colSpan={8} className="px-2 py-2">
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
              <ChildList title={`Barvy (${draft.barvy.length})`} items={draft.barvy} />
              <ChildList title={`Interiéry (${interiery.length})`} items={interiery} />
              <ChildList title={`Výbavy (${draft.vybavy.length})`} items={draft.vybavy} />
            </div>
          </td>
        </tr>
      )}
    </Fragment>
  );
}

function ChildList({ title, items }: { title: string; items: CenikImportItemDraft[] }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3">{title}</p>
      {items.length === 0 ? (
        <p className="mt-1 text-[11.5px] text-ink-3">—</p>
      ) : (
        <ul className="mt-1 flex flex-col gap-0.5">
          {items.map((it, i) => (
            <li key={i} className="flex items-baseline justify-between gap-2 text-[11.5px]">
              <span className="text-ink-2">{it.nazev}</span>
              <span className="shrink-0 font-mono tabular-nums text-ink-3">
                {formatInt(it.cenikova_cena)} Kč
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
